export type Plan = {
  id: number;
  name: string;
  description: string;
  duration_days: number | null;
  is_lifetime: boolean;
  price_stars: number;
  price_twd: number;
  enabled: boolean;
  featured: boolean;
  sort_order: number;
  gallery_images: string[];
};

export type PublicConfig = {
  stars_purchases_enabled?: boolean;
  bank_transfer_enabled?: boolean;
  appearance?: Record<string,string>;
  brand_name: string;
  brand_tagline: string;
  accent_color: string;
  group_title: string;
  support_url: string;
  group_url?: string | null;
  rules_text: string;
  bot_username: string;
  telegram_configured: boolean;
};

export type User = {
  id: number;
  telegram_id: string;
  username?: string | null;
  first_name: string;
  last_name?: string | null;
  photo_url?: string | null;
  bot_started_at?: string | null;
  is_admin: boolean;
};

export type Membership = {
  id: number;
  status: string;
  plan_id?: number | null;
  plan_name?: string | null;
  joined_at?: string | null;
  subscription_started_at?: string | null;
  expires_at?: string | null;
  is_lifetime: boolean;
  remaining_days: number | null;
  can_access_group: boolean;
};

export type Order = {
  id: string;
  plan_name: string;
  amount_stars: number;
  amount_twd?: number;
  review_note?: string;
  status: string;
  source: string;
  created_at: string;
  paid_at?: string | null;
};

function demoId() {
  const mode = new URLSearchParams(window.location.search).get('demo');
  if (mode) sessionStorage.setItem('club-preview-role', mode);
  return (mode || sessionStorage.getItem('club-preview-role')) === 'member' ? '910000000' : '900000001';
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const headers = new Headers(options.headers);
  headers.set('content-type', 'application/json');
  const initData = window.Telegram?.WebApp?.initData;
  if (initData) headers.set('x-telegram-init-data', initData);
  else headers.set('x-demo-user-id', demoId());
  const response = await fetch(path, { ...options, headers });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.error || `Request failed (${response.status})`);
  }
  if (response.status === 204) return undefined as T;
  return response.json();
}

export async function downloadCsv(dataset: string) {
  const headers = new Headers();
  const initData = window.Telegram?.WebApp?.initData;
  if (initData) headers.set('x-telegram-init-data', initData);
  else headers.set('x-demo-user-id', demoId());
  const response = await fetch(`/api/admin/export/${dataset}.csv`, { headers });
  if (!response.ok) throw new Error('匯出失敗');
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${dataset}-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

export const api = {
  beginBrowserLogin: () => request<any>('/api/browser-auth/begin',{method:'POST',body:'{}'}),
  completeBrowserLogin: (data:any) => request<{ready:boolean}>('/api/browser-auth/complete',{method:'POST',body:JSON.stringify(data)}),
  browserLogout: () => request<any>('/api/browser-auth/logout',{method:'POST',body:'{}'}),
  bankSettings: () => request<any>('/api/bank/settings'),
  bankReceipt: (id:string,admin=false) => request<{data:string|null}>(`/api/${admin?'admin/':''}bank/orders/${id}/receipt`),
  saveBankReceipt: (id:string,data:string|null) => request<{data:string|null}>(`/api/bank/orders/${id}/receipt`,{method:'POST',body:JSON.stringify({data})}),
  bankOrders: () => request<any[]>('/api/bank/orders'),
  createBankOrder: (planId:number) => request<any>('/api/bank/orders',{method:'POST',body:JSON.stringify({plan_id:planId})}),
  reportBankOrder: (id:string,data:any) => request<any>(`/api/bank/orders/${id}/report`,{method:'POST',body:JSON.stringify(data)}),
  cancelBankOrder: (id:string) => request<any>(`/api/bank/orders/${id}/cancel`,{method:'POST',body:'{}'}),
  bankInvite: () => request<{invite_url:string}>('/api/bank/invite',{method:'POST',body:'{}'}),
  adminBankSettings: () => request<any>('/api/admin/bank/settings'),
  saveBankSettings: (data:any) => request<any>('/api/admin/bank/settings',{method:'PUT',body:JSON.stringify(data)}),
  adminBankOrders: (status:string,search='') => request<any>(`/api/admin/bank/orders?${new URLSearchParams({status,search})}`),
  reviewBankOrder: (id:string,data:any) => request<any>(`/api/admin/bank/orders/${id}/review`,{method:'POST',body:JSON.stringify(data)}),
  retryBankNotification: (id:string) => request<any>(`/api/admin/bank/orders/${id}/retry`,{method:'POST',body:'{}'}),
  appearance: () => request<any>('/api/admin/appearance'),
  saveAppearance: (value: Record<string,string>,revision: number,publish = false) => request<any>(`/api/admin/appearance${publish ? '/publish' : ''}`,{method:publish?'POST':'PUT',body:JSON.stringify({value,revision})}),
  uploadAppearance: (data: string) => request<{url:string}>('/api/admin/appearance/assets',{method:'POST',body:JSON.stringify({data})}),
  avatar: () => request<{ photo_url: string | null }>('/api/me/avatar'),
  publicBootstrap: () => request<{ config: PublicConfig; plans: Plan[] }>('/api/public/bootstrap'),
  session: () => request<{ user: User; is_admin: boolean; demo: boolean; config: PublicConfig }>('/api/session'),
  me: () => request<{ user: User; membership: Membership | null; orders: Order[]; events: unknown[]; group: PublicConfig }>('/api/me'),
  invite: () => request<{ invite_url: string; expires_at: string }>('/api/me/invite', { method: 'POST', body: '{}' }),
  createInvoice: (planId: number) => request<any>('/api/orders/invoice', { method: 'POST', body: JSON.stringify({ plan_id: planId }) }),
  demoPay: (orderId: string) => request<any>(`/api/orders/${orderId}/demo-pay`, { method: 'POST', body: '{}' }),
  dashboard: () => request<any>('/api/admin/dashboard'),
  members: (params: URLSearchParams) => request<any>(`/api/admin/members?${params}`),
  member: (id: number) => request<any>(`/api/admin/members/${id}`),
  extendMember: (id: number, payload: any) => request<any>(`/api/admin/members/${id}/extend`, { method: 'POST', body: JSON.stringify(payload) }),
  moderateMember: (id: number, action: string) => request<any>(`/api/admin/members/${id}/moderate/${action}`, { method: 'POST', body: '{}' }),
  notifyMember: (id: number, message: string) => request<any>(`/api/admin/members/${id}/notify`, { method: 'POST', body: JSON.stringify({ message }) }),
  adminPlans: () => request<Plan[]>('/api/admin/plans'),
  savePlan: (plan: Partial<Plan>) => plan.id
    ? request<Plan>(`/api/admin/plans/${plan.id}`, { method: 'PUT', body: JSON.stringify(plan) })
    : request<Plan>('/api/admin/plans', { method: 'POST', body: JSON.stringify(plan) }),
  disablePlan: (id: number) => request<void>(`/api/admin/plans/${id}`, { method: 'DELETE' }),
  settings: () => request<any>('/api/admin/settings'),
  saveSettings: (payload: any) => request<any>('/api/admin/settings', { method: 'PUT', body: JSON.stringify(payload) }),
  checkSettings: () => request<any>('/api/admin/settings/check', { method: 'POST', body: '{}' }),
  publishEntry: () => request<any>('/api/admin/settings/publish-entry', { method: 'POST', body: '{}' }),
  requestGroupBind: () => request<{ ok: boolean; bot_username: string }>('/api/admin/settings/bind-request', { method: 'POST', body: '{}' }),
  unbindGroup: () => request<any>('/api/admin/settings/unbind', { method: 'POST', body: '{}' }),
  bindGroupManually: (chatId: string) => request<any>('/api/admin/settings/bind-manual', { method: 'POST', body: JSON.stringify({ chat_id: chatId }) }),
  memberModeration: (id: number) => request<any>(`/api/admin/members/${id}/moderation`),
  muteMember: (id: number, minutes: number | null, reason: string) => request<any>(`/api/admin/members/${id}/mute`, { method: 'POST', body: JSON.stringify({ minutes, reason }) }),
  unmuteMember: (id: number) => request<any>(`/api/admin/members/${id}/unmute`, { method: 'POST', body: '{}' }),
  warnMember: (id: number, reason: string) => request<any>(`/api/admin/members/${id}/warn`, { method: 'POST', body: JSON.stringify({ reason }) }),
  clearWarnings: (id: number) => request<any>(`/api/admin/members/${id}/warnings/clear`, { method: 'POST', body: '{}' }),
  security: () => request<any>('/api/admin/security'),
  blockedTerms: () => request<any[]>('/api/admin/blocked-terms'),
  saveBlockedTerm: (term: any) => term.id
    ? request<any>(`/api/admin/blocked-terms/${term.id}`, { method: 'PUT', body: JSON.stringify(term) })
    : request<any>('/api/admin/blocked-terms', { method: 'POST', body: JSON.stringify(term) }),
  deleteBlockedTerm: (id: number) => request<void>(`/api/admin/blocked-terms/${id}`, { method: 'DELETE' }),
  admins: () => request<any[]>('/api/admin/admins'),
  addAdmin: (payload: { telegram_id?: string; username?: string; role?: string }) => request<any>('/api/admin/admins', { method: 'POST', body: JSON.stringify(payload) }),
  removeAdmin: (id: number) => request<void>(`/api/admin/admins/${id}`, { method: 'DELETE' }),
  orders: (status = '') => request<any>(`/api/admin/orders${status ? `?status=${status}` : ''}`),
  refundOrder: (id: string) => request<any>(`/api/admin/orders/${id}/refund`, { method: 'POST', body: '{}' }),
  replies: () => request<any[]>('/api/admin/replies'),
  saveReply: (rule: any) => rule.id
    ? request<any>(`/api/admin/replies/${rule.id}`, { method: 'PUT', body: JSON.stringify(rule) })
    : request<any>('/api/admin/replies', { method: 'POST', body: JSON.stringify(rule) }),
  deleteReply: (id: number) => request<void>(`/api/admin/replies/${id}`, { method: 'DELETE' }),
  notifications: () => request<any[]>('/api/admin/notifications'),
  createNotification: (payload: any) => request<any>('/api/admin/notifications', { method: 'POST', body: JSON.stringify(payload) }),
  sendNotification: (id: number) => request<any>(`/api/admin/notifications/${id}/send`, { method: 'POST', body: '{}' }),
  audit: () => request<any[]>('/api/admin/audit')
};

export function formatDate(value?: string | null, withTime = false) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('zh-TW', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    ...(withTime ? { hour: '2-digit', minute: '2-digit', hour12: false } : {})
  }).format(date);
}

export const statusLabel: Record<string, string> = {
  pending: '待訂閱', active: '有效', expiring: '即將到期', expired: '已到期',
  kicked: '已移出', banned: '已封鎖', left: '已退出', paid: '付款成功', draft: '草稿',
  queued: '排程中', sending: '發送中', completed: '已完成'
  ,submitted:'待審核',needs_info:'待補資料',cancelled:'已取消',refunded:'已退款'
};
